import React, { useEffect, useState } from "react";
import { CircleX, Filter } from "lucide-react";
import { Button, Popover, OverlayTrigger, Form, Spinner } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { useNavigate, useLocation } from "react-router-dom";
import { useGetStudents } from "@/hooks/students";
import { useCourses } from "@/hooks/courses";
import { useGetCommittees } from "@/hooks/committees";
import { useLabels } from "@/hooks/labels";
import { useDashboardsInCategories } from "@/hooks/dashboard";
import { CustomSelectWrapper, type LabelValueOption } from "Components/Common/CustomSelectWrapper";

interface FilterButtonProps {
	urlParams?: URLSearchParams;
    onApplyFilter?: (filters: FilterState) => void;
    onResetFilter?: () => void;
}
interface FilterState {
    status: string;
    created: [start: Date | null, end: Date | null] | null;
    evaluation_date: [start: Date | null, end: Date | null] | null;
    student: string;
    course: string;
    committees: string;
    labels: string;
    dashboard: string;
    strategic_plan_ruuid: string;
    strategic_plan_name: string;
}

/** Standard status options always available. */
const BASE_STATUS_OPTIONS: { value: string; label: string }[] = [
    { value: "all", label: "All" },
    { value: "open", label: "Open" },
    { value: "goal-met", label: "Achieved" },
    { value: "goal-not-met", label: "Unmet" },
];

/** Extended status options available when filtering from an Effectiveness Plan drill-down. */
const DRILLDOWN_STATUS_OPTIONS: { value: string; label: string }[] = [
    { value: "all", label: "All" },
    { value: "open_time", label: "Open (on time)" },
    { value: "open_late", label: "Overdue" },
    { value: "closed_goal_met", label: "Achieved" },
    { value: "closed_goal_not_met", label: "Unmet" },
    { value: "no_date_assigned", label: "No Date Assigned" },
];

const popperConfig = {
    modifiers: [
      {
        name: "flip",
        options: {
          fallbackPlacements: ["top-end"]
        },
      },
      {
        name: "preventOverflow",
        options: {
          boundary: "viewport",
        },
      },
    ],
};

const FilterButton = ({ onApplyFilter, onResetFilter, urlParams }: FilterButtonProps) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [show, setShow] = useState(false);
    const [filters, setFilters] = useState<FilterState>({
        created: null,
        evaluation_date: null,
        status: "",
        student: "",
        course: "",
        committees: "",
        labels: "",
        dashboard: "",
        strategic_plan_ruuid: "",
        strategic_plan_name: "",
    });

    const isDrillDown = filters.strategic_plan_ruuid !== "";
    const statusOptions = isDrillDown ? DRILLDOWN_STATUS_OPTIONS : BASE_STATUS_OPTIONS;

    const isFilterApplied = () =>
        filters.created !== null ||
        filters.evaluation_date !== null ||
        filters.status !== "" ||
        filters.student !== "" ||
        filters.course !== "" ||
        filters.committees !== "" ||
        filters.labels !== "" ||
        filters.dashboard !== "" ||
        filters.strategic_plan_ruuid !== "";

    const handleReset = (field?: keyof FilterState) => {
        if (field) {
            const stringFields: (keyof FilterState)[] = ["status", "student", "course", "committees", "labels", "dashboard", "strategic_plan_ruuid", "strategic_plan_name"];
            if (field === "strategic_plan_ruuid") {
                // Clearing the plan also clears plan name and status (since drill-down status options change)
                setFilters((prev) => ({ ...prev, strategic_plan_ruuid: "", strategic_plan_name: "", status: "" }));
            } else {
                setFilters((prev) => ({ ...prev, [field]: stringFields.includes(field) ? "" : null }));
            }
        } else {
            setFilters({ created: null, evaluation_date: null, status: "", student: "", course: "", committees: "", labels: "", dashboard: "", strategic_plan_ruuid: "", strategic_plan_name: "" });
        }

        if (onResetFilter) {
            onResetFilter();
        }
    };

    const handleApplyFilter = () => {
        if (onApplyFilter) {
            onApplyFilter(filters);
        }
        setShow(false);
    };

    const handleSelect = (field: keyof FilterState, value: string) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
	};

	const parseDate = (value: string | null): Date | null => {
		return value ? new Date(value) : null;
    };

    // students list for dropdown
    const { data: studentsResp, isLoading: isStudentsLoading } = useGetStudents();
    const studentOptions = studentsResp?.data?.map((s) => ({
        label: s.student_name,
        value: s.ruuid,
    })) as LabelValueOption[] | undefined;
    // courses list for dropdown
    const { data: coursesResp } = useCourses();
    const courseOptions = coursesResp?.data?.map((c) => ({
        label: c.name,
        value: c.id,
    })).filter(Boolean) as LabelValueOption[] | undefined;

    // committees list for dropdown
    const { data: committeesResp, isLoading: isCommitteesLoading } = useGetCommittees();
    const committeeOptions = committeesResp?.data?.map((c) => ({
        label: c.name,
        value: c.id.toString(),
    })).filter(Boolean) as LabelValueOption[] | undefined;
    // labels list for dropdown
    const { data: labelsResp } = useLabels();
    const labelOptions = labelsResp?.data?.map((l) => ({
        label: l.name,
        value: l.id,
    })).filter(Boolean) as LabelValueOption[] | undefined;
    // dashboards list for dropdown
    const { dataFlat: dashboards, isLoading: isDashboardsLoading } = useDashboardsInCategories();
    const dashboardOptions = dashboards?.map((d) => ({
        label: d.name,
        value: d.id.toString(),
    })) as LabelValueOption[] | undefined;

    // readback
    useEffect(() => {
        const status = urlParams?.get("status") || "";
        const student = urlParams?.get("student") || "";
        const course = urlParams?.get("course") || "";
        const committees = urlParams?.get("committees") || "";
        const labels = urlParams?.get("labels") || "";
        const dashboard = urlParams?.get("dashboard") || "";
        const strategic_plan_ruuid = urlParams?.get("strategic_plan_ruuid") || "";
        const strategic_plan_name = urlParams?.get("strategic_plan_name") || "";

        const created: [Date | null, Date | null] | null = [
            parseDate(urlParams?.get("created_start") as string),
            parseDate(urlParams?.get("created_end") as string),
        ];

        const evaluation_date: [Date | null, Date | null] | null = [
            parseDate(urlParams?.get("evaluation_start") as string) || null,
            parseDate(urlParams?.get("evaluation_end") as string) || null,
        ];

        const isParamsPresent = status || student || course || committees || labels || dashboard || strategic_plan_ruuid || created[0] || created[1] || evaluation_date[0] || evaluation_date[1];

        setFilters({
            status,
            student,
            course,
            committees,
            labels,
            dashboard,
            strategic_plan_ruuid,
            strategic_plan_name,
            created: created[0] || created[1] ? created : null,
            evaluation_date: evaluation_date[0] || evaluation_date[1] ? evaluation_date : null,
        });

        // apply filter on page load
        if (onApplyFilter && isParamsPresent) {
            onApplyFilter({
                status,
                student,
                course,
                committees,
                labels,
                dashboard,
                strategic_plan_ruuid,
                strategic_plan_name,
                created: created || null,
                evaluation_date: evaluation_date || null,
            });
        }
    }, []);

    useEffect(() => {
        if (filters.status) {
            urlParams?.set("status", filters.status);
		} else {
			urlParams?.delete("status");
		}

        if (filters.student) {
            urlParams?.set("student", filters.student);
        } else {
            urlParams?.delete("student");
        }

        if (filters.course) {
            urlParams?.set("course", filters.course);
        } else {
            urlParams?.delete("course");
        }

        if (filters.committees) {
            urlParams?.set("committees", filters.committees);
        } else {
            urlParams?.delete("committees");
        }

        if (filters.labels) {
            urlParams?.set("labels", filters.labels);
        } else {
            urlParams?.delete("labels");
        }

        if (filters.dashboard) {
            urlParams?.set("dashboard", filters.dashboard);
        } else {
            urlParams?.delete("dashboard");
        }

        if (filters.strategic_plan_ruuid) {
            urlParams?.set("strategic_plan_ruuid", filters.strategic_plan_ruuid);
        } else {
            urlParams?.delete("strategic_plan_ruuid");
        }

        if (filters.strategic_plan_name) {
            urlParams?.set("strategic_plan_name", filters.strategic_plan_name);
        } else {
            urlParams?.delete("strategic_plan_name");
        }

		if (filters.created) {
			const [start, end] = filters.created;
            if(start) urlParams?.set("created_start", start.toISOString());
            if(end) urlParams?.set("created_end", end.toISOString());
		} else {
			urlParams?.delete("created_start");
			urlParams?.delete("created_end");
		}

        if (filters.evaluation_date) {
            const [start, end] = filters.evaluation_date;
            if (start) urlParams?.set("evaluation_start", start.toISOString());
            if (end) urlParams?.set("evaluation_end", end.toISOString());
        } else {
            urlParams?.delete("evaluation_start");
            urlParams?.delete("evaluation_end");
        }

        navigate({
            pathname: location.pathname,
            search: urlParams?.toString(),
        });
	}, [filters]);


    const popover = (
        <Popover id="filter-popover" className="filter-popover shadow-md">
            <Popover.Header className="bg-white border-bottom d-flex align-items-center justify-content-between">
                <span className="fw-semibold">Filter by</span>
                <Button variant="link" className="p-0 text-dark" onClick={() => setShow(false)}>
                    <CircleX size={16} />
                </Button>
            </Popover.Header>
            <Popover.Body className="p-0" style={{ maxHeight: "400px", overflowY: "auto" }}>
                <div className="filter-group border-bottom px-3 py-2">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="fw-bold small">Created date range</span>

						<Button variant="link" className="reset-btn p-0" onClick={() => handleReset("created")}>
							Reset
						</Button>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <Form.Group>
                            <Form.Label>From</Form.Label>
                            <DatePicker
                                id="date-due-from"
                                placeholderText="mm/dd/yyyy"
                                selected={filters.created?.[0]}
                                onChange={(date) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        created: [date as Date, prev.created?.[1] as Date],
                                    }))
                                }
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>To</Form.Label>
                            <DatePicker
                                id="date-due-to"
                                placeholderText="mm/dd/yyyy"
                                selected={filters.created?.[1]}
                                onChange={(date: Date) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        created: [prev.created?.[0] as Date, date as Date],
                                    }))
                                }
                            />
                        </Form.Group>
                    </div>
                </div>

                <div className="filter-group border-bottom px-3 py-2">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="fw-bold small">Due date range</span>

						<Button variant="link" className="reset-btn p-0" onClick={() => handleReset("evaluation_date")}>
							Reset
						</Button>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <Form.Group>
                            <Form.Label>From</Form.Label>
                            <DatePicker
                                id="date-due-from"
                                placeholderText="mm/dd/yyyy"
                                selected={filters.evaluation_date?.[0]}
                                onChange={(date) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        evaluation_date: [date as Date, prev.evaluation_date?.[1] as Date],
                                    }))
                                }
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>To</Form.Label>
                            <DatePicker
                                id="date-due-to"
                                placeholderText="mm/dd/yyyy"
                                selected={filters.evaluation_date?.[1]}
                                onChange={(date: Date) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        evaluation_date: [prev.evaluation_date?.[0] as Date, date as Date],
                                    }))
                                }
                            />
                        </Form.Group>
                    </div>
                </div>

                <div className="filter-group border-bottom px-3 py-2">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="fw-bold medium">Status</span>
						<Button variant="link" className="reset-btn p-0" onClick={() => handleReset("status")}>
							Reset
						</Button>
                    </div>
                    <Form.Select className={filters.status ? "" : "placeholder-text"} value={filters.status} onChange={(e) => handleSelect("status", e.target.value)}>
                        <option value="" disabled>
                            Select one
                        </option>
                        {statusOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </Form.Select>
                </div>

                {isDrillDown && (
                    <div className="filter-group border-bottom px-3 py-2">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <span className="fw-bold medium">Effectiveness Plan</span>
                            <Button variant="link" className="reset-btn p-0" onClick={() => handleReset("strategic_plan_ruuid")}>
                                Reset
                            </Button>
                        </div>
                        <Form.Control
                            type="text"
                            readOnly
                            plaintext
                            value={filters.strategic_plan_name ? decodeURIComponent(filters.strategic_plan_name) : "Effectiveness Plan"}
                            className="small text-muted"
                        />
                    </div>
                )}

                <div className="filter-group border-bottom px-3 py-2">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="fw-bold medium">Dashboard Name</span>
                    </div>
                    {isDashboardsLoading ? (
                        <div className="d-flex align-items-center gap-2"><Spinner size="sm" animation="border" /> <span className="small text-muted">Loading dashboards…</span></div>
                    ) : (
                        <CustomSelectWrapper<LabelValueOption, false>
                            isClearable
                            placeholder="Select dashboard"
                            value={filters.dashboard ? dashboardOptions?.find(opt => opt.value === filters.dashboard) || null : null}
                            getOptionLabel={(opt) => (opt as LabelValueOption).label}
                            onChange={(opt) => handleSelect("dashboard", (opt as LabelValueOption | null)?.value || "")}
                            options={dashboardOptions || []}
                        />
                    )}
                </div>

                <div className="filter-group border-bottom px-3 py-2">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="fw-bold medium">Student Name</span>
                    </div>
                    {isStudentsLoading ? (
                        <div className="d-flex align-items-center gap-2"><Spinner size="sm" animation="border" /> <span className="small text-muted">Loading students…</span></div>
                    ) : (
                        <CustomSelectWrapper<LabelValueOption, false>
                            isClearable
                            placeholder="Select student"
                            value={filters.student ? {
                                label: studentOptions?.find(opt => opt.value === filters.student)?.label || filters.student,
                                value: filters.student,
                            } : null}
                            getOptionLabel={(opt) => (opt as LabelValueOption).label}
                            onChange={(opt) => handleSelect("student", (opt as LabelValueOption | null)?.value || "")}
                            options={studentOptions || []}
                        />
                    )}
                </div>

                <div className="filter-group border-bottom px-3 py-2">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="fw-bold medium">Course Name</span>
                    </div>
                    {!coursesResp ? (
                        <div className="d-flex align-items-center gap-2"><Spinner size="sm" animation="border" /> <span className="small text-muted">Loading courses…</span></div>
                    ) : (
                        <CustomSelectWrapper<LabelValueOption, false>
                            isClearable
                            placeholder="Select course"
                            value={filters.course ? {
                                label: courseOptions?.find(opt => opt.value === filters.course)?.label || filters.course,
                                value: filters.course,
                            } : null}
                            getOptionLabel={(opt) => (opt as LabelValueOption).label}
                            onChange={(opt) => handleSelect("course", (opt as LabelValueOption | null)?.value || "")}
                            options={courseOptions}
                        />
                    )}
                </div>

                <div className="filter-group border-bottom px-3 py-2">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="fw-bold medium">Committee Group</span>
                    </div>
                    {isCommitteesLoading ? (
                        <div className="d-flex align-items-center gap-2"><Spinner size="sm" animation="border" /> <span className="small text-muted">Loading committee groups…</span></div>
                    ) : (
                        <CustomSelectWrapper<LabelValueOption, false>
                            isClearable
                            placeholder="Select committee group"
                            value={filters.committees ? {
                                label: committeeOptions?.find(opt => opt.value === filters.committees)?.label || filters.committees,
                                value: filters.committees,
                            } : null}
                            getOptionLabel={(opt) => (opt as LabelValueOption).label}
                            onChange={(opt) => handleSelect("committees", (opt as LabelValueOption | null)?.value || "")}
                            options={committeeOptions || []}
                        />
                    )}
                </div>

                <div className="filter-group border-bottom px-3 py-2">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="fw-bold medium">Labels</span>
                    </div>
                    {!labelsResp ? (
                        <div className="d-flex align-items-center gap-2"><Spinner size="sm" animation="border" /> <span className="small text-muted">Loading labels…</span></div>
                    ) : (
                        <CustomSelectWrapper<LabelValueOption, false>
                            isClearable
                            placeholder="Select label"
                            value={filters.labels ? {
                                label: labelOptions?.find(opt => opt.value === filters.labels)?.label || filters.labels,
                                value: filters.labels,
                            } : null}
                            getOptionLabel={(opt) => (opt as LabelValueOption).label}
                            onChange={(opt) => handleSelect("labels", (opt as LabelValueOption | null)?.value || "")}
                            options={labelOptions}
                        />
                    )}
                </div>
            </Popover.Body>
            <div className="filter-footer d-flex gap-2 px-3 py-2 border-top bg-white">
                <Button variant="outline-secondary" className="filter-actions__reset flex-grow-1 rounded-2"
                        onClick={() => handleReset()}>
                    Reset All Filter
                </Button>
                <Button variant="light" className="filter-actions__apply flex-grow-1 rounded-2 text-dark"
                        onClick={handleApplyFilter} disabled={!isFilterApplied()}>
                    Apply Filter
                </Button>
            </div>
        </Popover>
    );

    return (
        <OverlayTrigger trigger="click" placement="bottom-end" flip={true} show={show} onToggle={setShow} overlay={popover} rootClose={false} popperConfig={popperConfig}>
            <Button id="ap-filter-menu" variant="outline-secondary" className={`${show ? "active" : ""} filter-toggle bg-white text-dark border d-flex align-items-center gap-2`}>
				<Filter className={show ? "filled" : ""} />
                <span className="fw-medium small">Add Filter</span>
            </Button>
        </OverlayTrigger>
    );
};

export default FilterButton;